/**
 * The image picker shared by the new-ticket form and the reply box.
 *
 * Deliberately plain: a button, a row of thumbnails, a remove control on each.
 * No drag-and-drop zone, because the thing a customer is most likely to attach
 * is a screenshot they just took, and every OS has a file dialog for that —
 * whereas a drop zone is a large piece of furniture on a form that usually gets
 * no attachment at all.
 *
 * Object URLs are revoked when an item is removed and when the component
 * unmounts. Skipping that leaks the whole image into the tab's memory for as
 * long as it stays open, which on a five-image ticket is measurable.
 */

import { Button } from "@/components/ui/button";
import {
  ACCEPTED_IMAGE_ACCEPT,
  type PickedAttachment,
  formatBytes,
  pickAttachments,
  releasePreviews,
} from "@/lib/attachments";
import { useToast } from "@/lib/toast";
import { ImagePlus, X } from "lucide-react";
import { useEffect, useRef } from "react";
import { MAX_ATTACHMENTS_PER_MESSAGE } from "../../shared/support";

export function AttachmentPicker({
  attachments,
  onChange,
  disabled = false,
}: {
  attachments: PickedAttachment[];
  onChange: (next: PickedAttachment[]) => void;
  disabled?: boolean;
}) {
  const { pushToast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  // Unmount cleanup only. Removing one item revokes its own URL in `remove`
  // below; doing it here on every change would revoke URLs still on screen.
  const latest = useRef(attachments);
  latest.current = attachments;
  useEffect(() => {
    return () => releasePreviews(latest.current);
  }, []);

  async function onPick(files: FileList | null) {
    if (!files || files.length === 0) return;
    const result = await pickAttachments(files, attachments.length);
    // Always clear the input, so picking the same file twice in a row still
    // fires a change event.
    if (inputRef.current) inputRef.current.value = "";

    if (!result.ok) {
      pushToast({ kind: "error", title: result.error });
      return;
    }
    onChange([...attachments, ...result.attachments]);
  }

  function remove(id: string) {
    const going = attachments.find((a) => a.id === id);
    if (going) releasePreviews([going]);
    onChange(attachments.filter((a) => a.id !== id));
  }

  const full = attachments.length >= MAX_ATTACHMENTS_PER_MESSAGE;

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_ACCEPT}
        multiple
        className="sr-only"
        onChange={(e) => void onPick(e.target.files)}
        disabled={disabled || full}
        aria-hidden="true"
        tabIndex={-1}
      />

      <Button
        variant="outline"
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || full}
      >
        <ImagePlus aria-hidden="true" />
        {attachments.length === 0 ? "Attach a screenshot" : "Add another"}
      </Button>
      <span className="ml-3 text-[0.8125rem] text-ink-2">
        {full
          ? `That is the ${MAX_ATTACHMENTS_PER_MESSAGE}-image limit.`
          : "JPEG, PNG, WebP or GIF, up to 10 MB each."}
      </span>

      {attachments.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-3">
          {attachments.map((a) => (
            <li key={a.id} className="relative">
              <div className="w-28 overflow-hidden rounded-[2px] border border-rule bg-sunk">
                <img src={a.previewUrl} alt="" className="block h-20 w-full object-cover" />
                <div
                  className="truncate px-1.5 py-1 text-[0.6875rem] text-ink-2"
                  title={a.filename}
                >
                  {formatBytes(a.sizeBytes)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove(a.id)}
                disabled={disabled}
                aria-label={`Remove ${a.filename}`}
                className="absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full border border-rule-strong bg-card text-ink-2 transition-colors hover:border-ink hover:text-ink"
              >
                <X className="size-3" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
